/**
 * VatSummaryChangesModal — read-only diff view for VAT recapitulation changes.
 *
 * Translations are resolved via UIProvider under the key prefix
 * `components.vatSummaryChangesModal`.
 */

import { Modal, SimpleTable } from '../../components';
import { useTranslation } from '../../providers/UIProvider';
import type { Temporal } from '@insio/dates';
import { useFormatDotTimestamp } from '../../hooks/dates-hooks';
import { useFormatChangeValue, type FieldChange } from './LineChangesModal';

// ─── Exported types ───────────────────────────────────────────────────────────

export interface VatSummarySnapshot {
  vatRate: string | null;
  netAmount: string | null;
  vatAmount: string | null;
}

export interface VatSummaryModification {
  vatRate: string | null;
  fields: Record<string, FieldChange>;
}

export interface VatSummaryChangesData {
  added: VatSummarySnapshot[];
  removed: VatSummarySnapshot[];
  modified: VatSummaryModification[];
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface VatSummaryChangesModalProps {
  /** VAT summary change data from the invoice history entry */
  vatSummaryChanges: VatSummaryChangesData | null;
  /** Timestamp of the history entry — shown in the modal description */
  changedAt: Temporal.Instant | null | undefined;
  /** Whether the modal is visible */
  open: boolean;
  /** Called when the modal is dismissed */
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatVatRate(vatRate: string | null): string {
  if (!vatRate || vatRate.trim() === '') return '—';
  const trimmed = vatRate.trim();
  const n = Number(trimmed.replace(',', '.'));
  return Number.isNaN(n) ? trimmed : `${n} %`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ dot, label, count }: { dot: string; label: string; count: number }) {
  return (
    <div className='flex items-center gap-sm mb-sm'>
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
      <p className='text-xs font-semibold text-foreground-base'>
        {label}
        <span className='ml-xs text-foreground-muted font-normal'>({count})</span>
      </p>
    </div>
  );
}

interface SnapshotTableProps {
  rows: VatSummarySnapshot[];
  strikethrough?: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
  fmtChange: (v: string | null) => string;
}

function SnapshotTable({ rows, strikethrough, t, fmtChange }: SnapshotTableProps) {
  const dimmed = strikethrough ? 'text-foreground-muted line-through' : '';
  return (
    <SimpleTable
      scrollable
      headers={[
        <span className='text-left'>{t('fieldLabels.vatRate')}</span>,
        <span className='text-right block'>{t('fieldLabels.netAmount')}</span>,
        <span className='text-right block'>{t('fieldLabels.vatAmount')}</span>
      ]}
      data={rows}
      renderRow={row => [
        <span className={`font-semibold whitespace-nowrap text-foreground-base text-sm ${dimmed}`}>
          {formatVatRate(row.vatRate)}
        </span>,
        <span className={`block text-right tabular-nums whitespace-nowrap text-sm ${dimmed}`}>
          {row.netAmount != null ? fmtChange(row.netAmount) : '—'}
        </span>,
        <span className={`block text-right tabular-nums whitespace-nowrap text-sm ${dimmed}`}>
          {row.vatAmount != null ? fmtChange(row.vatAmount) : '—'}
        </span>
      ]}
    />
  );
}

interface ModifiedTableProps {
  mods: VatSummaryModification[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  fmtChange: (v: string | null) => string;
}

function ModifiedTable({ mods, t, fmtChange }: ModifiedTableProps) {
  type FlatRow = {
    vatRate: string | null;
    field: string;
    fieldLabel: string;
    change: FieldChange;
    isGroupStart: boolean;
  };

  const rows: FlatRow[] = mods.flatMap((mod, mi) =>
    Object.entries(mod.fields).map(([field, change], fi) => {
      const labelKey = `fieldLabels.${field}`;
      const translated = t(labelKey);
      return {
        vatRate: mod.vatRate,
        field,
        fieldLabel: translated !== labelKey ? translated : field,
        change,
        isGroupStart: fi === 0 && mi > 0
      };
    })
  );

  return (
    <SimpleTable
      scrollable
      headers={[
        <span className='text-left'>{t('fieldLabels.vatRate')}</span>,
        <span className='text-left'>{t('modal.field')}</span>,
        <span className='text-right block'>{t('modal.from')}</span>,
        <span className='text-right block'>{t('modal.to')}</span>
      ]}
      data={rows}
      getRowClassName={row => (row.isGroupStart ? 'border-t-2 border-border-strong' : '')}
      renderRow={(row, i) => {
        const isFirstField = i === 0 || rows[i - 1]?.vatRate !== row.vatRate || row.isGroupStart;
        return [
          <span className='font-semibold whitespace-nowrap text-foreground-base text-sm'>
            {isFirstField ? formatVatRate(row.vatRate) : ''}
          </span>,
          <span className='whitespace-nowrap text-foreground-muted text-sm'>{row.fieldLabel}</span>,
          <span className='block text-right line-through text-foreground-muted tabular-nums whitespace-nowrap text-sm'>
            {row.change.from != null ? fmtChange(row.change.from) : '—'}
          </span>,
          <span className='block text-right font-semibold tabular-nums whitespace-nowrap text-sm'>
            {row.change.to != null ? (
              fmtChange(row.change.to)
            ) : (
              <span className='italic font-normal text-foreground-muted'>
                {t('vatChanges.fieldCleared')}
              </span>
            )}
          </span>
        ];
      }}
    />
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function VatSummaryChangesModal({
  vatSummaryChanges,
  changedAt,
  open,
  onClose
}: VatSummaryChangesModalProps) {
  const t = useTranslation('components.vatSummaryChangesModal');
  const formatTs = useFormatDotTimestamp();
  const fmtChange = useFormatChangeValue();

  const addedCount = vatSummaryChanges?.added?.length ?? 0;
  const removedCount = vatSummaryChanges?.removed?.length ?? 0;
  const modifiedCount = vatSummaryChanges?.modified?.length ?? 0;
  const isEmpty = addedCount === 0 && removedCount === 0 && modifiedCount === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='2xl'
      className='w-[calc(100vw-2rem)]! md:w-200! max-h-[90vh] flex flex-col overflow-hidden'
    >
      <Modal.Header
        onClose={onClose}
        label={t('modal.title')}
        description={changedAt ? formatTs(changedAt) : undefined}
      />
      <Modal.Content className='p-md! flex-1 overflow-y-auto min-h-0'>
        <div className='w-full space-y-lg'>
          {addedCount > 0 && (
            <div>
              <SectionLabel
                dot='bg-success-text'
                label={t('modal.addedRates')}
                count={addedCount}
              />
              <SnapshotTable rows={vatSummaryChanges!.added} t={t} fmtChange={fmtChange} />
            </div>
          )}

          {removedCount > 0 && (
            <div>
              <SectionLabel
                dot='bg-error-text'
                label={t('modal.removedRates')}
                count={removedCount}
              />
              <SnapshotTable
                rows={vatSummaryChanges!.removed}
                strikethrough
                t={t}
                fmtChange={fmtChange}
              />
            </div>
          )}

          {modifiedCount > 0 && (
            <div>
              <SectionLabel
                dot='bg-warn-text'
                label={t('modal.modifiedRates')}
                count={modifiedCount}
              />
              <ModifiedTable mods={vatSummaryChanges!.modified} t={t} fmtChange={fmtChange} />
            </div>
          )}

          {isEmpty && (
            <p className='text-sm text-foreground-muted text-center py-lg'>
              {t('modal.noDetails')}
            </p>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}

VatSummaryChangesModal.displayName = 'VatSummaryChangesModal';
